// src/services/videoTags.js — hashtags, and the safety notice a risky clip carries.
//
// ══ WHY THESE TWO LIVE TOGETHER ════════════════════════════════════════════
// Both are read out of one caption at the moment a VIDEO is published, both are
// drawn in the same strip under the author's name in the reels player, and both
// are written by three different composers — the phone, the browser and the
// admin portal. One module means one answer to "what is a tag" and one answer
// to "is this clip dangerous", instead of three that agree until they don't.
//
// 🟥 THIS FILE HAS TWO TWINS: app/services/videoTags.ts and
//    app/server/functions/videoTags.js. They cannot import each other — three
//    build systems, three module formats — so gtest executes all three over the
//    same table AND compares the lexicons structurally. If they ever disagree,
//    make them agree; do not relax the test.
//
// ══ VIDEOS ONLY, AND THAT IS DELIBERATE ════════════════════════════════════
// 🟥 A hashtag on a photo or a text post is a word with a hash in front of it.
//    The thing people search a tag FOR is clips, the reels player is where tags
//    are read, and a tag index that also contains every stray "#blessed" on a
//    status update is an index nobody can use. Same for the notice: a still
//    photograph of a lion is not an instruction to go and touch one.

import { isVideoPost } from './postGrid';

/* ── folding ──────────────────────────────────────────────────────────────
   🟥 ONE FOLD, USED BY BOTH HALVES AND BY ALL THREE TWINS. Matching a term
      against text that was normalised even slightly differently is how a
      detector passes every test and then misses "Fuego" on a real phone. */

/** The letters this product is actually written in: Latin (+ accents),
 *  Cyrillic, Arabic, Devanagari, Kana and CJK. Everything else is a separator. */
const WORD = 'A-Za-z0-9_\\u00C0-\\u024F\\u0400-\\u04FF\\u0600-\\u06FF\\u0900-\\u097F\\u3040-\\u30FF\\u4E00-\\u9FFF';
const NOT_WORD = new RegExp(`[^${WORD}]+`, 'g');

/**
 * Lowercase, accents removed, everything that is not a letter reduced to a
 * space, and a space at each end so a whole-word test is a plain `includes`.
 *
 * 🟢 THE `#` DISAPPEARS HERE, ON PURPOSE. A caption's tags are part of what it
 *    says — "#firebreathing" should read to the detector exactly as
 *    "firebreathing" does — so the tags need no second pass of their own.
 */
/* 🟥 `normalize` IS WRAPPED. It is ES6 core, but the JavaScript engine the phone
   ships with has shipped without parts of it before, and a throw here would take
   down every caption in the feed rather than degrade one match. Unnormalised
   text is a slightly worse comparison; a crash is not a comparison at all. */
function norm(s, form) {
  try { return s.normalize(form); } catch { return s; }
}

export function fold(s) {
  const t = norm(String(s || ''), 'NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(NOT_WORD, ' ')
    .trim();
  return t ? ` ${t} ` : '';
}

/* ── hashtags ─────────────────────────────────────────────────────────────── */

export const MAX_TAGS = 12;
export const MAX_TAG_LEN = 50;
const MIN_TAG_LEN = 2;

/* 🟥 NO UNICODE PROPERTY ESCAPES. `\p{L}` is the obvious way to write this and
   it is not available on every engine the phone ships with — a regex that
   throws at module load takes the whole screen with it. The explicit class
   above covers every alphabet this app is translated into. */
const TAG_SRC = `(^|[^${WORD}])#([${WORD}]{1,${MAX_TAG_LEN}})`;
/* 🟥 A FRESH REGEX PER CALL. A module-level /g/ regex carries `lastIndex`
   between calls, so the second reader of a caption starts halfway through it —
   a bug that only shows up once two features read the same string. */
const tagRe = () => new RegExp(TAG_SRC, 'g');
const ALL_DIGITS = /^[0-9]+$/;

/* 🟥 A URL FRAGMENT IS NOT A HASHTAG. `https://x.com/page#section` ends in a
   hash followed by a word, which is precisely the shape of a tag — so pasting
   a link used to file the post under whatever the anchor happened to be
   called. The links are blanked out before the scan, character for character,
   so every offset still points at the same place in the original string and
   `hashtagSegments` can keep slicing from it. */
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s]+/gi;
const maskUrls = (src) =>
  src.replace(URL_RE, (m) => new Array(m.length + 1).join(' '));

/**
 * The tags in a caption, in the order they were written, lowercased and
 * deduplicated.
 *
 * 🟢 A `#` HAS TO START A WORD. `a#b`, a colour like `#ff0000` and the fragment
 *    on the end of a url are all hashes in the middle of something else, and
 *    treating them as tags fills the index with noise nobody typed.
 *
 * 🟥 AND A NUMBER IS NOT A TAG. "#1" is almost always "number one" — a tag page
 *    for it would collect every "#1 fan" ever posted and mean nothing.
 */
export function parseHashtags(text) {
  const src = maskUrls(String(text || ''));
  const re = tagRe();
  const out = [];
  const seen = {};
  let m = re.exec(src);
  while (m) {
    const tag = norm(String(m[2] || ''), 'NFC').toLowerCase();
    if (tag.length >= MIN_TAG_LEN && !ALL_DIGITS.test(tag) && !seen[tag]) {
      seen[tag] = true;
      out.push(tag);
      if (out.length >= MAX_TAGS) break;
    }
    m = re.exec(src);
  }
  return out;
}

/**
 * Split a caption into runs so the tags inside it can be drawn as links.
 *
 * 🟥 THE SLICES COME FROM THE ORIGINAL STRING, NEVER FROM THE FOLDED ONE.
 *    Folding changes length — an accent is one character before and two after
 *    NFD — so building the visible text out of folded pieces silently rewrites
 *    what somebody wrote. Concatenating every segment must give back the exact
 *    caption, which is asserted rather than assumed.
 */
export function hashtagSegments(text) {
  const src = String(text || '');
  if (!src) return [];
  /* scanned over the masked copy, sliced out of the real one */
  const scan = maskUrls(src);
  const re = tagRe();
  const out = [];
  let at = 0;
  let m = re.exec(scan);
  while (m) {
    const lead = String(m[1] || '');
    const raw = String(m[2] || '');
    const tag = norm(raw, 'NFC').toLowerCase();
    const hashAt = m.index + lead.length;
    if (tag.length >= MIN_TAG_LEN && !ALL_DIGITS.test(tag)) {
      if (hashAt > at) out.push({ text: src.slice(at, hashAt), tag: null });
      const end = hashAt + 1 + raw.length;
      out.push({ text: src.slice(hashAt, end), tag });
      at = end;
    }
    m = re.exec(scan);
  }
  if (at < src.length) out.push({ text: src.slice(at), tag: null });
  return out;
}

/** What the search box is handed when a chip is tapped. */
export function hashtagQuery(tag) {
  const t = norm(String(tag || '').replace(/^#+/, '').trim(), 'NFC');
  return t ? `#${t.toLowerCase()}` : '';
}

/**
 * Is this search text a bare hashtag, and if so which one?
 *
 * 🟥 COMPARED IN THE SAME CASE THE TAG WAS NORMALISED TO. "#Fire" parses to
 *    "fire", and comparing the raw query with it said they were different — so
 *    a tag typed with a capital was treated as an ordinary word search.
 *
 * 🟥 AND THE COMPARISON IS THE WHOLE TEST. There was a `tags.length === 1`
 *    beside it, and it could never change an answer: the only way the query can
 *    equal "#" plus the first tag is if that tag is the entire query, which is
 *    one tag. "#fire #ice" fails the comparison, "#fire and more" fails the
 *    comparison, and a query that parses to no tags at all fails it too. The
 *    mutation sweep deleted the length check and changed nothing, so it is
 *    gone: a guard that is never exercised reads as protection and is not.
 */
export function hashtagOfQuery(raw) {
  const q = String(raw || '').trim();
  if (!/^#/.test(q)) return null;
  const tags = parseHashtags(q);
  return q.replace(/\s+/g, '').toLowerCase() === `#${tags[0]}` ? tags[0] : null;
}

/** The tags to store on a post — videos only, and only what was really typed. */
export function tagsForPost(text, isVideo) {
  return isVideo ? parseHashtags(text) : [];
}

/** The tags to draw for a post that has already been published. */
export function postHashtags(post) {
  if (!isVideoPost(post)) return [];
  const raw = Array.isArray(post?.hashtags) ? post.hashtags : [];
  const out = [];
  const seen = {};
  for (const t of raw) {
    const tag = norm(String(t || '').replace(/^#+/, ''), 'NFC').toLowerCase().slice(0, MAX_TAG_LEN);
    if (tag && !seen[tag]) { seen[tag] = true; out.push(tag); }
    if (out.length >= MAX_TAGS) break;
  }
  return out;
}

/**
 * The tags a post carries that its own caption does not already show.
 *
 * 🟢 THE TAGS LIVE IN THE SENTENCE, and drawing them a second time as chips
 *    underneath is how a four-word caption ends up with the same four words
 *    twice. A caption is linkified in place; this is only the remainder — tags
 *    that reached the document some other way, or a caption edited after the
 *    fact — so the strip appears exactly when it has something to add.
 */
export function orphanHashtags(post) {
  const seen = {};
  for (const t of parseHashtags(post?.content)) seen[t] = true;
  return postHashtags(post).filter((t) => !seen[t]);
}

/* ── the safety notice ────────────────────────────────────────────────────
   🟥 NOBODY TYPES THIS. A warning somebody has to remember to add is a warning
      on the posts that needed it least, and one they type themselves exists
      only in the language they typed it in — which is no use to nine tenths of
      this app. The clip is read instead, and the sentence shown is the
      viewer's own language every time.

   🟢 TWO TIERS, BECAUSE ONE WORD IS NOT EVIDENCE. "fire" is in "campfire
      songs", "fire truck" and "this beat is fire"; "knife" is in every cooking
      video ever made. So a WEAK term only counts when the caption also carries
      a CUE — the words people actually write when they know something is
      risky. A STRONG term needs no help: nobody writes "flamethrower" by
      accident. That distinction is the whole difference between a notice
      people trust and one they learn to ignore. */

export const SAFETY_KINDS = ['weapons', 'chemicals', 'fire', 'animals', 'heights', 'general'];

/* The cues, in all ten languages: somebody saying out loud that this is not to
   be copied. On their own they mean "general"; beside a weak term they say
   which kind. */
const CUES = [
  'do not try', 'dont try', 'do not attempt', 'dont attempt', 'not attempt this',
  'at home', 'at your own risk', 'dangerous', 'danger', 'risky', 'stunt', 'stunts',
  'professionals only', 'trained professional', 'trained professionals', 'do not repeat',
  'no lo intentes', 'peligroso', 'peligro', 'riesgo',
  'n essayez pas', 'ne pas reproduire', 'dangereux', 'danger',
  'nicht nachmachen', 'gefahrlich', 'gefahr',
  'nao tente', 'perigoso', 'perigo',
  'ne povtoryayte', 'ne probuyte', 'opasno', 'opasnost',
  'опасно', 'опасность', 'не повторять', 'не пробуйте',
  'خطير', 'خطر', 'لا تجرب', 'لا تحاول',
  '危险', '不要模仿', '请勿模仿', '危险动作',
  '危険', 'まねしないで', '真似しないで',
  'खतरनाक', 'खतरा', 'कोशिश मत',
];

/* 🟢 STRONG TERMS ARE LOCALISED; WEAK TERMS CARRY THE COMMON WORD IN EACH
   LANGUAGE. A tier-2 hit needs a cue anyway, and the cues above are localised,
   so a Spanish caption reading "peligroso" + "fuego" lands on `fire` exactly
   as an English one reading "dangerous" + "fire" does. */
const LEXICON = {
  weapons: {
    strong: [
      'knife throwing', 'throwing knives', 'knife trick', 'knife tricks', 'machete',
      'chainsaw', 'flick knife', 'sword swallowing', 'juggling knives', 'live round',
      'live rounds', 'loaded gun', 'crossbow', 'lanzamiento de cuchillos', 'motosierra',
      'tronconneuse', 'kettensage', 'бензопила', 'منشار كهربائي', '电锯', 'チェーンソー',
    ],
    weak: [
      'knife', 'knives', 'blade', 'sword', 'axe', 'gun', 'rifle', 'pistol',
      'cuchillo', 'espada', 'couteau', 'messer', 'faca', 'нож', 'سكين', '刀', 'ナイフ', 'चाकू',
    ],
  },
  chemicals: {
    strong: [
      'homemade explosive', 'homemade explosives', 'pipe bomb', 'thermite', 'nitroglycerin',
      'bleach and ammonia', 'mixing bleach', 'acid burn', 'sulfuric acid', 'sodium metal',
      'dry ice bomb', 'electric shock', 'live wire', 'live wires', 'mains voltage',
      'high voltage', 'alto voltaje', 'haute tension', 'hochspannung', 'высокое напряжение',
      'جهد عالي', '高压电', '高電圧',
    ],
    weak: [
      'acid', 'bleach', 'ammonia', 'chemical', 'chemicals', 'explosion', 'explosive',
      'acido', 'lejia', 'produit chimique', 'chemikalien', 'кислота', 'حمض', '化学', '化学薬品',
    ],
  },
  fire: {
    strong: [
      'fire breathing', 'fire breather', 'fire eating', 'fire eater', 'flamethrower',
      'flame thrower', 'blowtorch', 'blow torch', 'pyrotechnics', 'pyrotechnic',
      'molotov', 'set it on fire', 'set on fire', 'fire spinning', 'fire poi',
      'lanzallamas', 'pirotecnia', 'soplete', 'lance flammes', 'pyrotechnie', 'chalumeau',
      'flammenwerfer', 'pyrotechnik', 'lanca chamas', 'pirotecnia', 'огнемет', 'пиротехника',
      'قاذف اللهب', 'ألعاب نارية', '喷火', '烟花', '火吹き', '火炎放射', 'आग उगलना',
    ],
    weak: [
      'fire', 'flame', 'flames', 'burning', 'gasoline', 'petrol', 'kerosene', 'lighter fluid',
      'fuego', 'llamas', 'feu', 'flammes', 'feuer', 'flammen', 'fogo', 'chamas',
      'огн', 'пламен', 'пламя', 'نار', 'لهب', '火焰', '炎', 'आग',
    ],
  },
  animals: {
    strong: [
      'wild animal', 'wild animals', 'lion', 'lions', 'lioness', 'tiger', 'tigers',
      'bear', 'bears', 'crocodile', 'alligator', 'venomous snake', 'venomous',
      'rattlesnake', 'cobra', 'shark', 'sharks', 'scorpion', 'tarantula',
      'leon', 'tigre', 'oso', 'cocodrilo', 'tiburon', 'serpiente venenosa',
      'lowe', 'tiger', 'bar', 'hai', 'leao', 'urso', 'tubarao',
      'лев', 'тигр', 'медведь', 'акула', 'أسد', 'نمر', 'دب', 'قرش',
      '狮子', '老虎', '鲨鱼', 'ライオン', 'トラ', 'サメ', 'शेर', 'बाघ',
    ],
    weak: ['snake', 'snakes', 'serpiente', 'serpent', 'schlange', 'cobra', 'змея', 'ثعبان', '蛇', 'ヘビ', 'सांप'],
  },
  heights: {
    strong: [
      'rooftop jump', 'roof jump', 'free climbing', 'free solo', 'cliff jumping',
      'cliff diving', 'base jump', 'base jumping', 'parkour', 'backflip',
      'without a harness', 'no harness', 'no safety gear', 'tightrope', 'slackline',
      'salto desde un acantilado', 'sin arnes', 'sans harnais', 'ohne sicherung',
      'без страховки', 'بدون حبل أمان', '徒手攀岩', '跑酷', 'フリーソロ', 'パルクール',
    ],
    weak: [
      'rooftop', 'roof', 'cliff', 'ledge', 'balcony', 'crane', 'scaffolding',
      'azotea', 'acantilado', 'toit', 'falaise', 'dach', 'klippe', 'telhado',
      'крыша', 'سطح', '屋顶', '屋上', 'छत',
    ],
  },
  /* 🟢 THE CATCH-ALL, AND IT NEEDS NO SUBJECT AT ALL. Somebody writing "do not
     try this at home" has told you it is dangerous even when nothing in the
     lexicon recognises what they were doing — which will always be most of it,
     because the world contains more dangerous things than any word list. */
  general: { strong: [], weak: [] },
};

/* 🟥 CJK HAS NO SPACES, so a whole-word test can never match inside it. A term
   containing a character from a script that does not separate words is matched
   as a plain substring instead; everything else keeps its boundaries, which is
   what stops "gun" firing on "begun". */
const NO_SPACES = /[\u3040-\u30FF\u4E00-\u9FFF]/;

/* 🟥 AND ARABIC, RUSSIAN AND HINDI GLUE THINGS ON TO THE WORD ITSELF. Arabic
   writes "the fire" as النار — one token, article and all. Russian declines:
   огонь, огня, огнём. Hindi and the other two attach postpositions the same
   way. A whole-word test against a lexicon therefore finds only the dictionary
   form, which is the one form nobody writes in a caption — "خطير: اللعب مع
   النار" and "опасно, играем с огнём" both mean exactly what this detector
   exists to catch, and both used to come back clean.

   🟢 SO TERMS IN THOSE SCRIPTS MATCH INSIDE A WORD, above a length floor. Three
      characters is enough to be a stem rather than a syllable, and the terms
      that lose the most from this are WEAK ones — which still need a cue before
      they fire, so a stray substring hit on its own changes nothing. Latin and
      the space-separated part of the lexicon keep their boundaries, because
      there "gun" inside "begun" is a real and common mistake. */
const GLUED = /[\u0400-\u04FF\u0600-\u06FF\u0900-\u097F]/;
const GLUED_MIN = 3;

function hit(folded, term) {
  const t = fold(term).trim();
  if (!t) return false;
  if (NO_SPACES.test(t)) return folded.indexOf(t) >= 0;
  if (GLUED.test(t) && t.length >= GLUED_MIN) return folded.indexOf(t) >= 0;
  if (folded.indexOf(` ${t} `) >= 0) return true;
  /* 🟥 A HASHTAG IS A PHRASE WITH THE SPACES TAKEN OUT, and that is how people
     write the riskiest ones: "#firebreathing", "#freesolo", "#basejump". The
     lexicon is written the way the words are spelt, so every multi-word term is
     also tested with its spaces removed — still whole-word, so it cannot start
     matching inside something else.
     🟢 THE TEXT IS NOT DE-SPACED, ONLY THE TERM. Collapsing the caption instead
        would fuse every word to its neighbour and make "the fire" match terms
        no one wrote. */
  if (t.indexOf(' ') < 0) return false;
  const joined = t.split(' ').join('');
  return folded.indexOf(` ${joined} `) >= 0;
}

function any(folded, terms) {
  for (const term of terms) if (hit(folded, term)) return true;
  return false;
}

/**
 * Read a caption and decide whether the clip needs a notice, and which one.
 *
 * Returns a kind from SAFETY_KINDS, or null. The order of SAFETY_KINDS is the
 * priority order — a caption about a knife trick performed over a fire gets
 * `weapons`, deterministically, rather than whichever the object happened to
 * enumerate first.
 */
export function detectSafety(text) {
  const folded = fold(text);
  if (!folded) return null;
  const cued = any(folded, CUES);
  for (const kind of SAFETY_KINDS) {
    if (kind === 'general') continue;
    const lex = LEXICON[kind];
    if (any(folded, lex.strong)) return kind;
    if (cued && any(folded, lex.weak)) return kind;
  }
  return cued ? 'general' : null;
}

/** What to store on a post being published — videos only. */
export function safetyForPost(text, isVideo) {
  if (!isVideo) return null;
  const kind = detectSafety(text);
  return kind ? { kind } : null;
}

/**
 * What to draw on a post that has already been published.
 *
 * 🟥 THE VIDEO TEST IS APPLIED AGAIN ON THE WAY OUT. A document written by an
 *    older build, or by an admin who changed a post's media afterwards, can
 *    carry a notice on something that is no longer a clip — and a warning about
 *    imitating a photograph is the kind of nonsense that teaches people to stop
 *    reading warnings.
 */
export function postSafetyKind(post) {
  if (!isVideoPost(post)) return null;
  const kind = post?.safety?.kind;
  return SAFETY_KINDS.indexOf(String(kind)) >= 0 ? kind : null;
}

/** The i18n keys for a kind — the app resolves these, the browser has a table. */
export function safetyKeys(kind) {
  return { title: 'vibe_safety_title', body: `vibe_safety_${kind}` };
}

/** For gtest: the data the three twins have to agree on, byte for byte. */
export const _LEXICON = LEXICON;
export const _CUES = CUES;
